import { Card, CardContent } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { CsvFile } from '@r/components/import/csv-file.tsx';
import { ImportErrorPage } from '@r/components/import/import-error-page.tsx';
import { AddPerformanceDialog } from '@admin/components/forms/performances/add-performance-dialog.tsx';
import { unparse } from 'papaparse';
import { useFileParserWithErrors } from '@r/hooks/use-file-parser-patch-with-errors.ts';
import { useImportState } from '@r/hooks/use-import-state.ts';
import { perfsImportBody } from '@r/api/generated-zod/index.ts';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { isTargetLogicWorkspace } from '@r/utils/workspace-utils.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';
import { useProductReferenceEnabled } from '@r/hooks/use-product-reference-enabled.ts';
import { toast } from 'sonner';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { ChartLineIcon, FilePlusIcon, Plus } from 'lucide-react';

export default function PerformancesImport() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const { workspace, isWorkspaceSuccess } = UseWorkspace(user.data);
  const { sanitizedWorkspaceName } = useWorkspaceName();
  const { state, setErrorState, setPreviewState, resetState } = useImportState('performances');
  const isProductReferenceEnabled = useProductReferenceEnabled();
  const [isAddPerformanceDialogOpen, setIsAddPerformanceDialogOpen] = useState(false);

  const isTargetLogic = useMemo(
    () => isWorkspaceSuccess && isTargetLogicWorkspace(workspace),
    [isWorkspaceSuccess, workspace]
  );

  // Utiliser le schéma Zod généré par Orval pour les données d'import de performances
  // Le schéma perfsImportBody a la structure { data: [...] }
  // Chaque item du tableau correspond à une ligne CSV
  const perfsImportSchema = perfsImportBody.shape.data.element;
  const { onParse } = useFileParserWithErrors(perfsImportSchema, '/admin/performances/import');

  const fields = ['amount', 'label', 'realizedAt', 'userUniqueCode', 'companyUniqueCode'];

  if (isTargetLogic) {
    fields.splice(2, 0, 'targetReference');
  }

  if (isProductReferenceEnabled) {
    fields.push('referenceUniqueCode', 'referenceQuantity');
  }

  const data = useMemo(() => {
    if (isTargetLogic && isProductReferenceEnabled) {
      // Target logic + Product reference enabled
      return [[100, 'Label Point 1', 'Global', '30/12/2021', 'UC01', 'CC01', 'PROD-REF-001', 2]];
    }

    if (isTargetLogic) {
      // Target logic only
      return [[100, 'Label Point 1', 'Global', '30/12/2021', 'UC01', 'CC01']];
    }

    if (isProductReferenceEnabled) {
      // Product reference enabled only
      return [
        [100, 'Label Point 1', '30/12/2021', 'UC01', 'CC01', 'PROD-REF-001', 2],
        [240, 'Label Point 2', '26/08/2021', 'UC02', 'CC02', 'PROD-REF-002', 1],
        [300, 'Label Point 3', '15/07/2021', 'UC03', 'CC03', 'PROD-REF-003', 3]
      ];
    }

    // Default case
    return [
      [100, 'Label Point 1', '30/12/2021', 'UC01', 'CC01'],
      [240, 'Label Point 2', '26/08/2021', 'UC02', 'CC02'],
      [300, 'Label Point 3', '15/07/2021', 'UC03', 'CC03'],
      [80, 'Label Point 4', '01/05/2021', 'UC04', 'CC04'],
      [509, 'Label Point 5', '01/01/2021', 'UC05', 'CC05']
    ];
  }, [isTargetLogic, isProductReferenceEnabled]);

  const csv = unparse({
    fields,
    data
  });

  const baseFilename = sanitizedWorkspaceName
    ? `${sanitizedWorkspaceName}_perf_import_example`
    : 'perf_import_example';

  const getFilenameSuffix = () => {
    if (isTargetLogic && isProductReferenceEnabled) {
      return '_obj_ref';
    }
    if (isTargetLogic) {
      return '_obj';
    }
    if (isProductReferenceEnabled) {
      return '_ref';
    }
    return '';
  };

  const filename = `${baseFilename}${getFilenameSuffix()}.csv`;

  const handleParse = async (files: File[]) => {
    const toastId = toast.loading(t('import.process.analyzing'));

    try {
      const result = await onParse(files);

      if (result.isPreview) {
        toast.dismiss(toastId);
        setPreviewState(result.errors ?? [], files[0], result.analysis ?? null);
      } else if (result.success) {
        toast.success(result.message || t('import.process.success'), { id: toastId });
        resetState();
      } else if (result.errors && result.errors.length > 0) {
        toast.dismiss(toastId);
        setErrorState(result.errors, files[0]);
      }
    } catch (error) {
      showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
        id: toastId
      });
    }
  };

  const handleRetry = async () => {
    if (state.originalFile) {
      const toastId = toast.loading(t('import.process.loading'));

      try {
        const result = await onParse([state.originalFile], true);

        if (result.success) {
          toast.success(result.message || t('import.process.success'), { id: toastId });
          resetState();
        } else {
          toast.error(result.message || t('import.process.error'), { id: toastId });
          if (result.errors && result.errors.length > 0) {
            setErrorState(result.errors, state.originalFile);
          }
        }
      } catch (error) {
        showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
          id: toastId
        });
      }
    }
  };

  if (state.isPreview && state.originalFile) {
    return (
      <ImportErrorPage
        errors={state.errors}
        originalFile={state.originalFile}
        onRetry={handleRetry}
        onNewFile={resetState}
        importType="performances"
        analysis={state.analysis}
      />
    );
  }

  return (
    <div className="space-y-10">
      <h1 className="text-4xl font-bold">{t('common.add.performance')}</h1>

      {/* Bloc 1 : Ajouter des performances (formulaire en modal) */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-2 items-center">
            <ChartLineIcon className="w-5 h-5" />
            <h2 className="text-xl font-semibold">{t('common.add.performance')}</h2>
          </div>
          <p className="text-muted-foreground">{t('admin.performances.add.block.description')}</p>
          <Button
            rounded="full"
            onClick={() => setIsAddPerformanceDialogOpen(true)}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="mr-2 w-4 h-4" />
            {t('common.add.performance')}
          </Button>
        </CardContent>
      </Card>

      <AddPerformanceDialog
        isOpen={isAddPerformanceDialogOpen}
        onOpenChange={setIsAddPerformanceDialogOpen}
      />

      {/* Bloc 2 : Importer des performances (CSV) */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-2 items-center">
            <FilePlusIcon className="w-5 h-5" />
            <h2 className="text-xl font-semibold">{t('admin.performances.import.block.title')}</h2>
          </div>
          <p className="text-muted-foreground">
            {t('admin.performances.import.block.description')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('admin.performances.import.block.example')}
          </p>
          <CsvFile csv={csv} filename={filename} label={t('common.csv.example.button')} />

          <FileUploader
            accept={{
              'text/csv': [],
              'application/vnd.ms-excel': [],
              'application/csv': [],
              'text/x-csv': [],
              'application/x-csv': [],
              'text/comma-separated-values': [],
              'text/x-comma-separated-values': []
            }}
            onParse={handleParse}
          />
        </CardContent>
      </Card>
    </div>
  );
}
